<script lang="ts">
	// UC-35: the Discount sheet (T14), over Sell.
	//
	// Two taps from Sell: Discount, then the discount. The owner's list comes
	// with the menu, each with what it takes off this cart, and None first. One
	// tap gives it and closes the sheet; an order takes one discount, so a new
	// one replaces the last. The till works the amount out with the server's
	// own rule (domain/pricing.ts), and the server works it out again.
	//
	// A manager also gets Type a discount: a percent or an amount on the
	// keypad, and Confirm. A percent is a whole number up to 100; an amount
	// fills the minor places from the right, as the payment keypad does. A
	// cashier never sees the way in, and the server refuses one anyway.

	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import Keypad from '../components/Keypad.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Sheet from '../components/Sheet.svelte';
	import {
		discountMinor,
		MAX_DISCOUNT_PERCENT,
		type DiscountKind,
		type OrderDiscount,
	} from '../domain/pricing';
	import { discountRate } from '../format/discount';
	import { formatMinor, parseTypedMinor } from '../format/money';
	import { fill, message, type Language } from '../i18n/messages';
	import type { components } from '../api/types';

	type Listed = components['schemas']['DiscountBody'];

	interface Props {
		open: boolean;
		language: Language;
		minorDigits: number;
		/** What the discount comes off: the subtotal and the delivery fee. */
		baseMinor: number;
		/** The owner's list, as the menu read gave it. */
		discounts: Listed[];
		/** The discount the cart has now, or null. */
		current: OrderDiscount | null;
		isManager: boolean;
		onPick: (discount: OrderDiscount | null) => void;
		onClose: () => void;
	}

	let {
		open,
		language,
		minorDigits,
		baseMinor,
		discounts,
		current,
		isManager,
		onPick,
		onClose,
	}: Props = $props();

	const NONE = 'none';
	const TYPED = 'typed';

	// A percent never needs more than three digits; an amount stops at eight,
	// far under the most a discount may be.
	const MAX_DIGITS: Record<DiscountKind, number> = { percent: 3, amount: 8 };

	// Each time the sheet opens or closes, a lock included, it starts again on
	// the list with nothing typed, so the next cashier never finds a manager's
	// half-typed discount. Typing overrides both until then.
	let step = $derived.by<'list' | 'type'>(() => {
		void open;
		return 'list';
	});
	let kind = $state<DiscountKind>('percent');
	let typed = $derived.by(() => {
		void open;
		return '';
	});

	const typedCurrent = $derived(current && !current.discountId ? current : null);
	const sorted = $derived(
		[...discounts].sort((a, b) => a.name.localeCompare(b.name, language === 'en' ? 'en' : 'ar')),
	);

	const options = $derived([
		{ value: NONE, label: message(language, 'discountNone'), testId: 'give-discount-none' },
		...sorted.map((d) => ({
			value: d.id,
			label: d.name,
			sub: discountRate(language, d.kind, d.value, minorDigits),
			price: formatMinor(-discountMinor(baseMinor, d.kind, d.value, minorDigits), minorDigits),
			testId: `give-discount-${d.id}`,
		})),
		...(typedCurrent
			? [
					{
						value: TYPED,
						label: message(language, 'discountManager'),
						sub: discountRate(language, typedCurrent.kind, typedCurrent.value, minorDigits),
						price: formatMinor(
							-discountMinor(baseMinor, typedCurrent.kind, typedCurrent.value, minorDigits),
							minorDigits,
						),
						testId: 'give-discount-typed',
					},
				]
			: []),
	]);
	const picked = $derived(!current ? NONE : current.discountId ? current.discountId : TYPED);
	// None shows whenever the cart has a discount to take off, even when the
	// owner's list is empty: an API a revert put back sends no list, and the
	// cart's discount must still come off to sell (INT-7).
	const hasOptions = $derived(sorted.length > 0 || current !== null);

	function pick(value: string | string[]) {
		if (value === TYPED) {
			onClose();
			return;
		}
		const listed = sorted.find((d) => d.id === value);
		onPick(
			listed
				? { discountId: listed.id, name: listed.name, kind: listed.kind, value: listed.value }
				: null,
		);
	}

	// ---------- typing one (a manager) ----------

	const number = $derived(parseTypedMinor(typed) ?? 0);
	const valid = $derived(
		typed !== '' && number > 0 && (kind === 'amount' || number <= MAX_DISCOUNT_PERCENT),
	);
	const off = $derived(valid ? discountMinor(baseMinor, kind, number, minorDigits) : 0);
	const shown = $derived(
		kind === 'percent' ? (typed ? `${number}%` : '—') : formatMinor(number, minorDigits),
	);
	const takes = $derived(
		!valid
			? ''
			: baseMinor - off === 0
				? fill(message(language, 'discountTakesAll'), formatMinor(off, minorDigits))
				: fill(
						message(language, 'discountTakesOff'),
						formatMinor(off, minorDigits),
						formatMinor(baseMinor - off, minorDigits),
					),
	);

	function startTyping() {
		// A manager who typed one already starts from it.
		kind = typedCurrent?.kind ?? 'percent';
		typed = typedCurrent ? String(typedCurrent.value) : '';
		step = 'type';
	}

	function chooseKind(next: string) {
		if (next === kind) return;
		kind = next as DiscountKind;
		typed = '';
	}

	function key(digit: string) {
		const next = (typed + digit).replace(/^0+(?=\d)/, '');
		if (next.length <= MAX_DIGITS[kind]) typed = next;
	}

	function confirm() {
		if (!valid || !isManager) return;
		onPick({ kind, value: number });
		step = 'list';
	}

	function close() {
		step = 'list';
		onClose();
	}
</script>

<Sheet
	{open}
	title={step === 'type' ? message(language, 'discountType') : message(language, 'discountTitle')}
	titleTestId="give-discount-title"
	meta={formatMinor(baseMinor, minorDigits)}
	metaLabel={message(language, 'discountBefore')}
	metaTestId="give-discount-base"
	backLabel={message(language, 'discountTitle')}
	onBack={step === 'type' ? () => (step = 'list') : undefined}
	backTestId="give-discount-back"
	primaryLabel={step === 'type' ? message(language, 'confirm') : undefined}
	primaryIcon="check"
	primaryTestId="give-discount-confirm"
	primaryDisabled={!valid || !isManager}
	onPrimary={confirm}
	secondaryLabel={step === 'list' && isManager ? message(language, 'discountType') : undefined}
	secondaryTestId="give-discount-type"
	onSecondary={startTyping}
	onClose={close}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="give-discount-close"
	testId="give-discount-sheet"
>
	{#if step === 'list'}
		<div class="list" data-scroll="">
			{#if sorted.length === 0}
				<p class="empty" data-testid="give-discount-empty">{message(language, 'discountEmpty')}</p>
			{/if}
			{#if hasOptions}
				<Choice
					mode="single"
					stack
					{options}
					value={picked}
					onChange={pick}
					testId="give-discount-choice"
				/>
			{/if}
		</div>
	{:else}
		<div class="split">
			<div class="column" data-scroll="">
				<Segmented
					label={message(language, 'discountsKind')}
					options={[
						{
							value: 'percent',
							label: message(language, 'discountsPercent'),
							testId: 'give-discount-kind-percent',
						},
						{
							value: 'amount',
							label: message(language, 'discountsAmount'),
							testId: 'give-discount-kind-amount',
						},
					]}
					value={kind}
					wide
					onChange={chooseKind}
				/>
				<Field
					testId="give-discount-value"
					label={kind === 'percent'
						? message(language, 'discountsPercentLabel')
						: message(language, 'discountsAmountLabel')}
					value={shown}
					mono
					note={kind === 'percent'
						? message(language, 'discountPercentNote')
						: message(language, 'discountAmountNote')}
					error={kind === 'percent' && number > MAX_DISCOUNT_PERCENT
						? message(language, 'discountsPercentRange')
						: undefined}
					errorTestId="give-discount-value-error"
				/>
				{#if takes}
					<p class="effect" role="status" data-testid="give-discount-effect">{takes}</p>
				{/if}
			</div>
			<div class="pad">
				<div class="fit">
					<Keypad
						testId="give-discount-pad"
						fit
						label={kind === 'percent'
							? message(language, 'discountsPercentLabel')
							: message(language, 'discountsAmountLabel')}
						deleteLabel={message(language, 'backspace')}
						canType={typed.length < MAX_DIGITS[kind]}
						canDelete={typed.length > 0}
						onKey={key}
						onBackspace={() => (typed = typed.slice(0, -1))}
					/>
				</div>
			</div>
		</div>
	{/if}
</Sheet>

<style>
	.list {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.empty {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.split {
		flex: 1;
		min-block-size: 0;
		display: flex;
		gap: var(--space-9);
	}

	.column {
		flex: 1 1 280px;
		min-inline-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.effect {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--text-strong);
	}

	.pad {
		flex: none;
		inline-size: calc(var(--key-w) * 3 + var(--touch-gap) * 2);
		min-block-size: 0;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.fit {
		flex: 1 1 0;
		min-block-size: 0;
	}
</style>
